import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Typography, Card, Descriptions, Spin, Alert, Button, Space, Tag, Select, message } from 'antd';
import { ArrowLeftOutlined, LinkOutlined } from '@ant-design/icons';
import { getUnifiedTask, updateUnifiedTask, taskModuleLabel, type UnifiedTaskRow } from '@/services/unifiedTask.service';
import { useUserDisplayResolver } from '@/hooks/useUserDisplayResolver';
import { isLikelyUserId } from '@/utils/userDisplay';

const { Title } = Typography;

const DELAY_REASON_OPTIONS = [
  { value: 'waiting_for_approval', label: 'Waiting for approval' },
  { value: 'waiting_for_supplier', label: 'Waiting for supplier' },
  { value: 'missing_document', label: 'Missing document' },
  { value: 'dependency_blocked', label: 'Dependency blocked' },
  { value: 'user_delay', label: 'User delay' },
  { value: 'other', label: 'Other' },
];

function openActionLink(navigate: ReturnType<typeof useNavigate>, link?: string) {
  if (!link) return;
  if (link.startsWith('http://') || link.startsWith('https://')) {
    window.open(link, '_blank', 'noopener,noreferrer');
    return;
  }
  navigate(link.startsWith('/') ? link : `/${link}`);
}

/**
 * Deep link for a single unified task (notifications, bookmarks).
 * Backend enforces company + module visibility.
 */
const UnifiedTaskDetailPage: React.FC = () => {
  const { taskId } = useParams<{ taskId: string }>();
  const navigate = useNavigate();
  const { resolve: resolveUserDisplay } = useUserDisplayResolver();
  const [task, setTask] = useState<UnifiedTaskRow | null>(null);
  const [loading, setLoading] = useState(true);
  const assigneeDisplay =
    task?.assigned_to_name && !isLikelyUserId(task.assigned_to_name)
      ? task.assigned_to_name
      : resolveUserDisplay(task?.assigned_to, 'Unknown user');

  useEffect(() => {
    if (!taskId) {
      setTask(null);
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    void getUnifiedTask(taskId)
      .then((row) => {
        if (!cancelled) setTask(row);
      })
      .catch(() => {
        if (!cancelled) setTask(null);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [taskId]);

  if (loading) {
    return (
      <div style={{ padding: 24, textAlign: 'center' }}>
        <Spin size="large" />
      </div>
    );
  }

  if (!task) {
    return (
      <div style={{ padding: 24 }}>
        <Alert
          type="error"
          message="Task not found"
          description="It may have been removed or you may not have access to this module."
          showIcon
        />
        <Button type="link" onClick={() => navigate('/home/tasks')}>
          Back to My Tasks
        </Button>
      </div>
    );
  }

  return (
    <div style={{ padding: 24 }}>
      <Space direction="vertical" size="large" style={{ width: '100%' }}>
        <Space wrap>
          <Button type="default" icon={<ArrowLeftOutlined />} onClick={() => navigate('/home/tasks')}>
            My tasks
          </Button>
        </Space>
        <Title level={3} style={{ margin: 0 }}>
          Task
        </Title>
        <Card>
          <Descriptions column={1} size="small" bordered>
            <Descriptions.Item label="Action">{task.recommended_action || '—'}</Descriptions.Item>
            <Descriptions.Item label="Module">{taskModuleLabel(task.module)}</Descriptions.Item>
            <Descriptions.Item label="Type">{task.task_type}</Descriptions.Item>
            <Descriptions.Item label="Status">
              <Tag>{task.status}</Tag>
            </Descriptions.Item>
            <Descriptions.Item label="Priority">{task.priority}</Descriptions.Item>
            <Descriptions.Item label="Assignee">{assigneeDisplay}</Descriptions.Item>
            <Descriptions.Item label="Due">{task.due_date || '—'}</Descriptions.Item>
            <Descriptions.Item label="Source">
              {task.source_document_type}
              {task.source_document_number ? ` · ${task.source_document_number}` : ''}
            </Descriptions.Item>
            <Descriptions.Item label="Party">{task.party_display_name || '—'}</Descriptions.Item>
            <Descriptions.Item label="Remarks">{task.remarks || '—'}</Descriptions.Item>
            <Descriptions.Item label="SLA expected (UTC)">
              {task.expected_completion_at || '—'}
            </Descriptions.Item>
            <Descriptions.Item label="SLA status">
              {task.sla_risk && task.sla_risk !== 'none' ? (
                <Tag
                  color={
                    task.sla_risk === 'critical'
                      ? 'magenta'
                      : task.sla_risk === 'breach'
                        ? 'red'
                        : task.sla_risk === 'warning'
                          ? 'orange'
                          : 'green'
                  }
                >
                  {task.sla_risk}
                </Tag>
              ) : (
                '—'
              )}
            </Descriptions.Item>
            <Descriptions.Item label="Escalation level">{task.escalation_level ?? 0}</Descriptions.Item>
            <Descriptions.Item label="Blocking tasks">
              {task.blocking_task_ids?.length
                ? task.blocking_task_ids.map((id) => (
                    <Button key={id} type="link" size="small" onClick={() => navigate(`/home/tasks/${id}`)}>
                      {id.slice(0, 8)}…
                    </Button>
                  ))
                : '—'}
            </Descriptions.Item>
            <Descriptions.Item label="Inferred delay reason">
              {task.system_inferred_reason_effective || '—'}
            </Descriptions.Item>
            <Descriptions.Item label="Delay reason (manual)">
              <Select
                allowClear
                placeholder="Select reason"
                style={{ minWidth: 220 }}
                value={task.delay_reason || undefined}
                options={DELAY_REASON_OPTIONS}
                onChange={async (v) => {
                  try {
                    const updated = await updateUnifiedTask(task.id, { delay_reason: v ?? null });
                    if (updated) {
                      setTask(updated);
                      message.success('Saved');
                    }
                  } catch {
                    message.error('Could not save');
                  }
                }}
              />
            </Descriptions.Item>
          </Descriptions>
          <Button
            type="primary"
            icon={<LinkOutlined />}
            style={{ marginTop: 16 }}
            onClick={() => openActionLink(navigate, task.action_link)}
            disabled={!task.action_link}
          >
            Open linked document
          </Button>
        </Card>
      </Space>
    </div>
  );
};

export default UnifiedTaskDetailPage;
